/**
 * Subtasks Mini-Grid Component
 *
 * A compact, inline checklist view for subtasks with:
 * - Checkboxes for quick completion toggle
 * - Inline title editing with auto-save
 * - Quick add new subtask in grid
 * - Expand button to open full drawer for detailed editing
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  Checkbox,
  Input,
  Button,
  Tooltip,
  Progress,
  Space,
  Popconfirm,
  Spin,
  message,
} from 'antd';
import type { InputRef } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ExpandOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DragOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { instanceSubtasksApi, templateSubtasksApi } from '@api/subtasks';
import type { InstanceSubtask, TemplateSubtask } from '@api/subtasks';
import { useAutoSave } from '@hooks/useAutoSave';

const { Text } = Typography;

// ============================================================================
// Subtask Row Component (shared between template and instance)
// ============================================================================

interface SubtaskRowProps {
  subtask: InstanceSubtask | TemplateSubtask;
  isInstance: boolean;
  parentId: string;
  onToggle?: (subtaskId: string) => void;
  onUpdate: (subtaskId: string, title: string) => Promise<void>;
  onDelete: (subtaskId: string) => void;
  onExpand?: (subtask: InstanceSubtask | TemplateSubtask) => void;
  isNew?: boolean;
  onCreated?: () => void;
  onCreate?: (title: string) => Promise<void>;
}

function SubtaskRow({
  subtask,
  isInstance,
  parentId,
  onToggle,
  onUpdate,
  onDelete,
  onExpand,
  isNew,
  onCreated,
  onCreate,
}: SubtaskRowProps) {
  const [title, setTitle] = useState(subtask.title || '');
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const hasCreatedRef = useRef(false);

  const isCompleted = isInstance ? (subtask as InstanceSubtask).isCompleted : false;
  const isRequired = subtask.isRequired;

  // Auto-save for existing subtasks
  const { save, status } = useAutoSave({
    saveFn: async (newTitle: string) => {
      await onUpdate(subtask.id, newTitle);
    },
    queryKey: isInstance ? ['instance-subtasks', parentId] : ['template-subtasks', parentId],
    debounceMs: 500,
  });

  // Focus input for new rows
  useEffect(() => {
    if (isNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isNew]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!isNew && value.trim()) {
      save(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isNew && title.trim() && onCreate && !hasCreatedRef.current) {
      hasCreatedRef.current = true;
      setIsCreating(true);
      onCreate(title.trim()).then(() => {
        setTitle('');
        setIsCreating(false);
        hasCreatedRef.current = false;
        onCreated?.();
      }).catch(() => {
        setIsCreating(false);
        hasCreatedRef.current = false;
      });
    }
    if (e.key === 'Escape' && isNew) {
      setTitle('');
    }
  };

  const handleBlur = () => {
    if (isNew && title.trim() && onCreate && !hasCreatedRef.current) {
      hasCreatedRef.current = true;
      setIsCreating(true);
      onCreate(title.trim()).then(() => {
        setTitle('');
        setIsCreating(false);
        hasCreatedRef.current = false;
        onCreated?.();
      }).catch(() => {
        setIsCreating(false);
        hasCreatedRef.current = false;
      });
    }
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger expand if clicking on interactive elements
    const target = e.target as HTMLElement;
    const isInteractive =
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.closest('button') ||
      target.closest('.ant-checkbox') ||
      target.closest('.ant-popconfirm');
    if (!isInteractive && !isNew && onExpand) {
      onExpand(subtask);
    }
  };

  return (
    <div
      className={`subtask-row ${isCompleted ? 'completed' : ''} ${isNew ? 'new-row' : ''}`}
      onClick={handleRowClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 4,
        backgroundColor: isNew ? '#fafafa' : 'transparent',
        borderBottom: '1px solid #f0f0f0',
        opacity: isCompleted ? 0.6 : 1,
        cursor: !isNew && onExpand ? 'pointer' : 'default',
      }}
    >
      {/* Drag handle (future: implement drag reordering) */}
      {!isNew && (
        <DragOutlined
          style={{ color: 'var(--text-muted)', cursor: 'grab', fontSize: 12 }}
        />
      )}

      {/* Checkbox (only for instance subtasks) */}
      {isInstance && !isNew && (
        <Checkbox
          checked={isCompleted}
          onChange={() => onToggle?.(subtask.id)}
          style={{ marginRight: 4 }}
        />
      )}

      {/* Required indicator */}
      {isRequired && !isNew && (
        <Tooltip title="Required">
          <ExclamationCircleOutlined style={{ color: 'var(--status-warning)', fontSize: 12 }} />
        </Tooltip>
      )}

      {/* Title input */}
      <Input
        ref={inputRef}
        value={title}
        onChange={(e) => handleTitleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={isNew ? handleBlur : undefined}
        placeholder={isNew ? 'Add subtask...' : 'Subtask title'}
        variant="borderless"
        style={{
          flex: 1,
          textDecoration: isCompleted ? 'line-through' : 'none',
          color: isCompleted ? 'var(--text-tertiary)' : 'inherit',
          padding: '2px 4px',
        }}
        disabled={isCreating}
      />

      {/* Status indicator for saving */}
      {status === 'saving' && (
        <Spin size="small" />
      )}
      {status === 'saved' && (
        <CheckCircleOutlined style={{ color: 'var(--status-success)', fontSize: 12 }} />
      )}

      {/* Actions */}
      {!isNew && (
        <Space size={4}>
          {/* Expand to drawer */}
          {onExpand && (
            <Tooltip title="Edit details">
              <Button
                type="text"
                size="small"
                icon={<ExpandOutlined />}
                onClick={() => onExpand(subtask)}
                style={{ opacity: 0.6 }}
              />
            </Tooltip>
          )}

          {/* Delete */}
          <Popconfirm
            title="Delete subtask?"
            onConfirm={() => onDelete(subtask.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                danger
                style={{ opacity: 0.6 }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )}

      {/* Add icon for new row */}
      {isNew && !isCreating && (
        <PlusOutlined style={{ color: '#bfbfbf', fontSize: 14 }} />
      )}
      {isCreating && <Spin size="small" />}
    </div>
  );
}

// ============================================================================
// Instance Subtasks Mini-Grid
// ============================================================================

interface InstanceSubtasksMiniGridProps {
  instanceId: string;
  onExpandSubtask?: (subtask: InstanceSubtask) => void;
  showProgress?: boolean;
  maxHeight?: number | string;
}

export function InstanceSubtasksMiniGrid({
  instanceId,
  onExpandSubtask,
  showProgress = true,
  maxHeight = 300,
}: InstanceSubtasksMiniGridProps) {
  const queryClient = useQueryClient();

  // Fetch subtasks
  const { data, isLoading } = useQuery({
    queryKey: ['instance-subtasks', instanceId],
    queryFn: () => instanceSubtasksApi.list(instanceId),
    enabled: !!instanceId,
  });

  const subtasks = data?.items || [];
  const progress = data?.progress || { total: 0, completed: 0, required: 0, incompleteRequired: 0 };

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: (subtaskId: string) => instanceSubtasksApi.toggle(instanceId, subtaskId),
    onMutate: async (subtaskId) => {
      await queryClient.cancelQueries({ queryKey: ['instance-subtasks', instanceId] });
      const previous = queryClient.getQueryData(['instance-subtasks', instanceId]);

      // Optimistic update
      type QueryData = { items: InstanceSubtask[]; progress: { total: number; completed: number; required: number; incompleteRequired: number } };
      queryClient.setQueryData<QueryData>(['instance-subtasks', instanceId], (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((s: InstanceSubtask) =>
            s.id === subtaskId ? { ...s, isCompleted: !s.isCompleted } : s
          ),
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['instance-subtasks', instanceId], context.previous);
      }
      message.error('Failed to update subtask');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['instance-subtasks', instanceId] });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ subtaskId, title }: { subtaskId: string; title: string }) =>
      instanceSubtasksApi.update(instanceId, subtaskId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instance-subtasks', instanceId] });
    },
    onError: () => {
      message.error('Failed to update subtask');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (title: string) => instanceSubtasksApi.create(instanceId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instance-subtasks', instanceId] });
      message.success('Subtask added');
    },
    onError: () => {
      message.error('Failed to add subtask');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (subtaskId: string) => instanceSubtasksApi.delete(instanceId, subtaskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instance-subtasks', instanceId] });
      message.success('Subtask deleted');
    },
    onError: () => {
      message.error('Failed to delete subtask');
    },
  });

  const handleToggle = useCallback((subtaskId: string) => {
    toggleMutation.mutate(subtaskId);
  }, [toggleMutation]);

  const handleUpdate = useCallback(async (subtaskId: string, title: string) => {
    await updateMutation.mutateAsync({ subtaskId, title });
  }, [updateMutation]);

  const handleDelete = useCallback((subtaskId: string) => {
    deleteMutation.mutate(subtaskId);
  }, [deleteMutation]);

  const handleCreate = useCallback(async (title: string) => {
    await createMutation.mutateAsync(title);
  }, [createMutation]);

  if (isLoading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <Spin size="small" />
      </div>
    );
  }

  const progressPercent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;

  return (
    <div className="subtasks-mini-grid">
      {/* Progress bar */}
      {showProgress && progress.total > 0 && (
        <div style={{ marginBottom: 12, padding: '0 8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {progress.completed}/{progress.total} completed
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {progressPercent}%
            </Text>
          </div>
          <Progress
            percent={progressPercent}
            size="small"
            showInfo={false}
            strokeColor={progress.incompleteRequired > 0 ? 'var(--status-warning)' : 'var(--status-success)'}
          />
          {progress.incompleteRequired > 0 && (
            <Text type="warning" style={{ fontSize: 11 }}>
              {progress.incompleteRequired} required item{progress.incompleteRequired > 1 ? 's' : ''} remaining
            </Text>
          )}
        </div>
      )}

      {/* Subtasks list */}
      <div
        style={{
          maxHeight,
          overflowY: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
        }}
      >
        {subtasks.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)' }}>
            No subtasks yet
          </div>
        )}

        {subtasks.map((subtask) => (
          <SubtaskRow
            key={subtask.id}
            subtask={subtask}
            isInstance={true}
            parentId={instanceId}
            onToggle={handleToggle}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onExpand={onExpandSubtask as ((subtask: InstanceSubtask | TemplateSubtask) => void) | undefined}
          />
        ))}

        {/* New subtask row */}
        <SubtaskRow
          key="new"
          subtask={{
            id: 'new',
            instanceId,
            templateSubtaskId: null,
            title: '',
            description: null,
            sortOrder: 0,
            isCompleted: false,
            completedAt: null,
            completedBy: null,
            isRequired: false,
            createdAt: '',
            updatedAt: '',
          } satisfies InstanceSubtask}
          isInstance={true}
          parentId={instanceId}
          onUpdate={async () => {}}
          onDelete={() => {}}
          isNew
          onCreate={handleCreate}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Template Subtasks Mini-Grid
// ============================================================================

interface TemplateSubtasksMiniGridProps {
  templateId: string;
  onExpandSubtask?: (subtask: TemplateSubtask) => void;
  maxHeight?: number | string;
}

export function TemplateSubtasksMiniGrid({
  templateId,
  onExpandSubtask,
  maxHeight = 300,
}: TemplateSubtasksMiniGridProps) {
  const queryClient = useQueryClient();

  // Fetch subtasks
  const { data: subtasks = [], isLoading } = useQuery({
    queryKey: ['template-subtasks', templateId],
    queryFn: () => templateSubtasksApi.list(templateId),
    enabled: !!templateId,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ subtaskId, title }: { subtaskId: string; title: string }) =>
      templateSubtasksApi.update(templateId, subtaskId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-subtasks', templateId] });
    },
    onError: () => {
      message.error('Failed to update subtask');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (title: string) => templateSubtasksApi.create(templateId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-subtasks', templateId] });
      message.success('Subtask added');
    },
    onError: () => {
      message.error('Failed to add subtask');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (subtaskId: string) => templateSubtasksApi.delete(templateId, subtaskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-subtasks', templateId] });
      message.success('Subtask deleted');
    },
    onError: () => {
      message.error('Failed to delete subtask');
    },
  });

  const handleUpdate = useCallback(async (subtaskId: string, title: string) => {
    await updateMutation.mutateAsync({ subtaskId, title });
  }, [updateMutation]);

  const handleDelete = useCallback((subtaskId: string) => {
    deleteMutation.mutate(subtaskId);
  }, [deleteMutation]);

  const handleCreate = useCallback(async (title: string) => {
    await createMutation.mutateAsync(title);
  }, [createMutation]);

  if (isLoading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <Spin size="small" />
      </div>
    );
  }

  return (
    <div className="subtasks-mini-grid">
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {subtasks.length} subtask{subtasks.length !== 1 ? 's' : ''} defined
        </Text>
      </div>

      {/* Subtasks list */}
      <div
        style={{
          maxHeight,
          overflowY: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
        }}
      >
        {subtasks.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)' }}>
            No subtasks defined. Add subtasks that will be copied to task instances.
          </div>
        )}

        {subtasks.map((subtask) => (
          <SubtaskRow
            key={subtask.id}
            subtask={subtask}
            isInstance={false}
            parentId={templateId}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onExpand={onExpandSubtask as ((subtask: InstanceSubtask | TemplateSubtask) => void) | undefined}
          />
        ))}

        {/* New subtask row */}
        <SubtaskRow
          key="new"
          subtask={{
            id: 'new',
            templateId,
            title: '',
            description: null,
            sortOrder: 0,
            isRequired: false,
            createdAt: '',
            updatedAt: '',
          } satisfies TemplateSubtask}
          isInstance={false}
          parentId={templateId}
          onUpdate={async () => {}}
          onDelete={() => {}}
          isNew
          onCreate={handleCreate}
        />
      </div>
    </div>
  );
}

export default InstanceSubtasksMiniGrid;
